import { Task } from '@st/redux'
import type { AuthError } from '@st/sdk'
import { Effect } from 'effect'
import { match } from '@st/pattern'
import { Auth, SdkClient, Snackbar } from '~/effects'
import { runSdkRequest } from '~/features/module-runtime'

type TwoFactorFailure = { type: 'twoFactorFailed'; error: AuthError }

export type AccountSettingsOutcome =
  | { type: 'settingsUpdated' }
  | { type: 'settingsUpdateFailed' }
  | { type: 'profileNameSaved' }
  | { type: 'profileNameSaveFailed' }
  | { type: 'enableTwoFactorCodeSent' }
  | { type: 'enableTwoFactorSucceeded' }
  | { type: 'disableTwoFactorCodeSent' }
  | { type: 'disableTwoFactorSucceeded' }
  | TwoFactorFailure

export const loadAccountSettings = Task.define((_userId: string) =>
  Effect.gen(function* () {
    const sdk = yield* SdkClient
    const settings = yield* runSdkRequest(sdk.send({ type: 'accounts/getAccountSettings' }))
    if (!settings.ok) return { type: 'settingsLoadFailed' } as const
    return {
      type: 'settingsLoaded',
      emailNotificationsEnabled: settings.response.emailNotificationsEnabled
    } as const
  })
)

export function updateEmailNotifications(enabled: boolean) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const snackbar = yield* Snackbar
    const status = match(enabled)
      .with(true, () => 'enabled')
      .with(false, () => 'disabled')
      .exhaustive()
    const updated = yield* runSdkRequest(
      sdk.send({
        type: 'accounts/updateAccountSettings',
        emailNotificationsEnabled: enabled
      })
    )
    if (!updated.ok) {
      yield* snackbar.show('Failed to update account settings', { variant: 'error' })
      return { type: 'settingsUpdateFailed' } as const
    }

    yield* snackbar.show(`Email notifications ${status}`)
    return { type: 'settingsUpdated' } as const
  })
}

export function saveProfileName(userId: string, name: string) {
  return Effect.gen(function* () {
    const auth = yield* Auth
    const snackbar = yield* Snackbar

    return yield* auth.setProfileName(userId, name).pipe(
      Effect.andThen(snackbar.show('Full name updated successfully')),
      Effect.as({ type: 'profileNameSaved' } as const),
      Effect.catchTag('AuthFailure', () =>
        snackbar
          .show('Failed to update full name', { variant: 'error' })
          .pipe(Effect.as({ type: 'profileNameSaveFailed' } as const))
      )
    )
  })
}

export function sendEnableTwoFactorCode(phoneNumber: string, currentPassword: string) {
  return Effect.gen(function* () {
    const auth = yield* Auth
    const update = yield* auth.updatePhoneNumber({
      phoneNumber,
      currentPassword
    })
    if (update.status != 'success') {
      const error = yield* requireAuthError(update.error, 'updatePhoneNumber')
      return twoFactorFailed(error)
    }

    yield* auth.sendPhoneNumberVerificationCode()
    return { type: 'enableTwoFactorCodeSent' } as const
  }).pipe(Effect.catchTag('AuthFailure', (error) => Effect.succeed(twoFactorFailed(error))))
}

export function verifyEnableTwoFactorCode(code: string) {
  return Effect.gen(function* () {
    const auth = yield* Auth
    const verification = yield* auth.verifyPhoneNumber(code)
    if (verification.status != 'success') {
      const error = yield* requireAuthError(verification.error, 'verifyPhoneNumber')
      return twoFactorFailed(error)
    }

    yield* auth.enableMultiFactorAuthentication('phone_number')
    return { type: 'enableTwoFactorSucceeded' } as const
  }).pipe(Effect.catchTag('AuthFailure', (error) => Effect.succeed(twoFactorFailed(error))))
}

export function sendDisableTwoFactorCode() {
  return Effect.gen(function* () {
    const auth = yield* Auth
    yield* auth.sendPhoneNumberVerificationCode()
    return { type: 'disableTwoFactorCodeSent' } as const
  }).pipe(Effect.catchTag('AuthFailure', (error) => Effect.succeed(twoFactorFailed(error))))
}

export function disableTwoFactor(currentPassword: string, code: string) {
  return Effect.gen(function* () {
    const auth = yield* Auth
    const update = yield* auth.updatePhoneNumber({
      phoneNumber: '',
      currentPassword,
      code
    })
    if (update.status != 'success') {
      const error = yield* requireAuthError(update.error, 'disableTwoFactor')
      return twoFactorFailed(error)
    }

    return { type: 'disableTwoFactorSucceeded' } as const
  }).pipe(Effect.catchTag('AuthFailure', (error) => Effect.succeed(twoFactorFailed(error))))
}

function twoFactorFailed(error: AuthError): TwoFactorFailure {
  return { type: 'twoFactorFailed', error }
}

function requireAuthError(
  error: AuthError | undefined,
  operation: string
): Effect.Effect<AuthError> {
  if (!error) {
    return Effect.die(new Error(`${operation} failed without an AuthError`))
  }
  return Effect.succeed(error)
}
